import { ChatConfigApi } from '@myre/app-agent-generated/sdk/angular/ChatConfigApi';
import { DynamicEnvService } from '@myre/ui-domain-http';

import { inject, Injectable, signal } from '@angular/core';
import { filter, from, switchMap, take } from 'rxjs';

import { ChatContextService } from './services/chat-context.service';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly _dynamicEnvService = inject(DynamicEnvService);
  private readonly _chatConfigApi = inject(ChatConfigApi);
  private readonly _chatContextService = inject(ChatContextService);

  private readonly _opened = signal(false);
  private readonly _showChat = signal(false);

  /**
   * Read-only signal indicating whether the chat is opened.
   */
  public readonly opened = this._opened.asReadonly();

  /**
   * Read-only signal indicating whether the chat feature is available.
   * Do not use backend-agent root API because of the interceptor that display error on a modal.
   */
  public readonly showChat = this._showChat.asReadonly();

  constructor() {
    this._checkChatAvailability();
  }

  public open(): void {
    this._opened.set(true);
  }

  public close(): void {
    this._opened.set(false);
  }

  public toggle(): void {
    this._opened.set(!this._opened());
  }

  private _checkChatAvailability(): void {
    const host = this._dynamicEnvService.env.myre.agent?.back?.host;

    if (!host) {
      return;
    }

    // Skip agent calls on non-customer routes (e.g. /admin/*)
    this._chatContextService.contextForApi$
      .pipe(
        filter(context => !!context.params.customerId),
        take(1),
        switchMap(context =>
          from(globalThis.fetch(`${host}/`, { credentials: 'include' })).pipe(
            filter(res => res.ok),
            switchMap(() => this._chatConfigApi.getConfig(context)),
          ),
        ),
      )
      .subscribe({
        next: () => this._showChat.set(true),
        error: () => this._showChat.set(false),
      });
  }
}
